import React, { useEffect, useMemo, useState } from "react";
import {
  AlertTriangle,
  CheckCircle2,
  Loader2,
  LogIn,
  LogOut,
  MapPin,
  Navigation,
  Radar,
  Route,
  ShieldAlert,
  Target,
} from "lucide-react";
import {
  Button,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
  FormField,
  Progress,
} from "../ui";
import { cn } from "../../lib/utils";
import { obterConfiguracaoCheckTarefa } from "../../services/tarefaService";
import {
  DEFAULT_TASK_GEO_RADIUS_METERS,
  TASK_GEO_POSITION_OPTIONS,
  calculateGeoDistanceMeters,
  formatGeoAccuracy,
  formatGeoCoordinate,
  formatGeoDistance,
  resolveGeoBrowserError,
} from "../../utils/taskGeoCheck";

function GeoStatCard({ icon: Icon, label, value, helper, tone = "default" }) {
  const toneClassName = {
    success: "border-success/30 bg-success/10 text-success",
    warning: "border-warning/30 bg-warning/10 text-warning",
    danger: "border-destructive/30 bg-destructive/10 text-destructive",
    info: "border-info/30 bg-info/10 text-info",
    default: "border-border bg-muted/20 text-foreground",
  }[tone] || "border-border bg-muted/20 text-foreground";

  return (
    <article className={cn("rounded-2xl border p-3 shadow-sm", toneClassName)}>
      <div className="flex items-start gap-3">
        <div className="mt-0.5 rounded-2xl border border-current/20 p-2">
          <Icon className="h-4 w-4" />
        </div>
        <div className="min-w-0">
          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] opacity-75">{label}</p>
          <p className="mt-1 text-sm font-semibold">{value}</p>
          {helper ? <p className="mt-1 text-xs opacity-80">{helper}</p> : null}
        </div>
      </div>
    </article>
  );
}

export default function TaskGeoCheckDrawer({
  open,
  onOpenChange,
  action,
  task,
  partner,
  canSubmit,
  busy = false,
  fallbackEnabled = false,
  fallbackRequiresImage = false,
  fallbackRequiresApproval = false,
  fallbackState,
  fallbackForm,
  fallbackPreviews = [],
  onFallbackMotivoChange,
  onFallbackFilesChange,
  onConfirm,
  onSubmitFallback,
}) {
  const [radiusMeters, setRadiusMeters] = useState(DEFAULT_TASK_GEO_RADIUS_METERS);
  const [configLoading, setConfigLoading] = useState(false);
  const [currentGeo, setCurrentGeo] = useState(null);
  const [geoLoading, setGeoLoading] = useState(false);
  const [geoError, setGeoError] = useState("");

  const actionLabel = action === "checkout" ? "check-out" : "check-in";
  const ActionIcon = action === "checkout" ? LogOut : LogIn;
  const fallbackAtivo = Boolean(fallbackEnabled && fallbackState?.habilitado && fallbackState?.acao === action);
  const partnerHasCoordinates = Number.isFinite(Number(partner?.latitude)) && Number.isFinite(Number(partner?.longitude));

  useEffect(() => {
    let active = true;

    if (!open) {
      return undefined;
    }

    setConfigLoading(true);
    obterConfiguracaoCheckTarefa()
      .then((config) => {
        if (!active) return;
        setRadiusMeters(Number(config?.radiusMeters) > 0 ? Number(config.radiusMeters) : DEFAULT_TASK_GEO_RADIUS_METERS);
      })
      .catch(() => {
        if (!active) return;
        setRadiusMeters(DEFAULT_TASK_GEO_RADIUS_METERS);
      })
      .finally(() => {
        if (active) {
          setConfigLoading(false);
        }
      });

    return () => {
      active = false;
    };
  }, [open]);

  useEffect(() => {
    if (!open) {
      setCurrentGeo(null);
      setGeoLoading(false);
      setGeoError("");
      return undefined;
    }

    if (!navigator.geolocation) {
      setGeoLoading(false);
      setGeoError("Geolocalizacao indisponivel neste navegador");
      return undefined;
    }

    let active = true;
    setGeoLoading(true);
    setGeoError("");

    const watchId = navigator.geolocation.watchPosition(
      (position) => {
        if (!active) return;
        setCurrentGeo({
          lat: position.coords.latitude,
          lng: position.coords.longitude,
          accuracyM: position.coords.accuracy,
          timestamp: position.timestamp,
        });
        setGeoLoading(false);
        setGeoError("");
      },
      (error) => {
        if (!active) return;
        setGeoLoading(false);
        setGeoError(resolveGeoBrowserError(error));
      },
      TASK_GEO_POSITION_OPTIONS
    );

    return () => {
      active = false;
      navigator.geolocation.clearWatch(watchId);
    };
  }, [open]);

  const distanceMeters = useMemo(
    () =>
      calculateGeoDistanceMeters(currentGeo?.lat, currentGeo?.lng, partner?.latitude, partner?.longitude),
    [currentGeo?.lat, currentGeo?.lng, partner?.latitude, partner?.longitude]
  );

  const insideRadius = useMemo(() => {
    if (!Number.isFinite(distanceMeters)) {
      return null;
    }
    return Number(distanceMeters) <= Number(radiusMeters);
  }, [distanceMeters, radiusMeters]);

  const distanceProgress = useMemo(() => {
    if (!Number.isFinite(distanceMeters) || !Number.isFinite(radiusMeters) || Number(radiusMeters) <= 0) {
      return 0;
    }

    const ratio = Math.min(1, Number(distanceMeters) / Number(radiusMeters));
    return Math.max(6, Math.round((1 - ratio) * 100));
  }, [distanceMeters, radiusMeters]);

  const distanceTone = insideRadius == null ? "info" : insideRadius ? "success" : "danger";
  const canConfirm = Boolean(open && task?.id && canSubmit && !busy && currentGeo);
  const canSubmitFallback =
    Boolean(fallbackAtivo && !busy && canSubmit && String(fallbackForm?.motivo || "").trim())
    && (!fallbackRequiresImage || (fallbackForm?.files || []).length > 0);

  const helperStatusText = useMemo(() => {
    if (geoError) {
      return geoError;
    }
    if (!partnerHasCoordinates) {
      return "O parceiro nao possui coordenadas registradas. A validacao final continua no backend.";
    }
    if (insideRadius == null) {
      return geoLoading ? "Capturando localizacao atual..." : "Aguardando localizacao do dispositivo.";
    }
    return insideRadius
      ? "Dentro do raio oficial configurado para o check."
      : "Fora do raio oficial. O backend continua sendo a validacao final.";
  }, [geoError, geoLoading, insideRadius, partnerHasCoordinates]);

  const handleConfirm = async () => {
    if (!canConfirm || !onConfirm) {
      return;
    }

    await onConfirm({
      action,
      geo: {
        lat: currentGeo.lat,
        lng: currentGeo.lng,
        accuracyM: currentGeo.accuracyM,
        distanceM: Number.isFinite(distanceMeters) ? distanceMeters : null,
      },
    });
  };

  const handleSubmitFallback = async () => {
    if (!canSubmitFallback || !onSubmitFallback) {
      return;
    }
    await onSubmitFallback({ action });
  };

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent>
        <DrawerHeader>
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-3xl border border-gold/35 bg-gold/15 text-gold">
            <ActionIcon className="h-6 w-6" />
          </div>
          <DrawerTitle className="mt-4">Validar {actionLabel} por geolocalizacao</DrawerTitle>
          <DrawerDescription>
            A distancia exibida abaixo e uma previa visual. A confirmacao oficial continua sendo feita pelo backend.
          </DrawerDescription>
        </DrawerHeader>

        <DrawerBody className="space-y-5">
          <section className="rounded-[28px] border border-border/80 bg-background/80 p-4 shadow-sm">
            <div className="text-center">
              <p className="text-xs font-semibold uppercase tracking-[0.24em] text-muted-foreground">Distancia atual</p>
              <div className="mt-3 text-5xl font-semibold tracking-tight text-foreground">
                {partnerHasCoordinates && currentGeo ? formatGeoDistance(distanceMeters) : "--"}
              </div>
              <p className="mt-2 text-sm text-muted-foreground">{helperStatusText}</p>
            </div>

            <div className="mx-auto mt-5 max-w-md space-y-2">
              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <span>Raio oficial</span>
                <span>
                  {configLoading ? "Carregando..." : formatGeoDistance(radiusMeters)}
                </span>
              </div>
              <Progress
                value={distanceProgress}
                indicatorClassName={
                  insideRadius == null
                    ? "bg-info"
                    : insideRadius
                      ? "bg-success"
                      : "bg-destructive"
                }
              />
            </div>

            <div className="mt-5 grid gap-3 sm:grid-cols-2">
              <GeoStatCard
                icon={Target}
                label="Status"
                value={
                  insideRadius == null
                    ? geoLoading
                      ? "Capturando GPS"
                      : "Aguardando leitura"
                    : insideRadius
                      ? "Dentro do raio"
                      : "Fora do raio"
                }
                helper={partnerHasCoordinates ? "Comparacao ao vivo entre usuario e parceiro." : "Sem referencia valida do parceiro."}
                tone={distanceTone}
              />
              <GeoStatCard
                icon={Radar}
                label="Precisao do GPS"
                value={currentGeo ? formatGeoAccuracy(currentGeo.accuracyM) : "--"}
                helper={currentGeo ? `Atualizado em ${new Date(currentGeo.timestamp).toLocaleTimeString("pt-BR")}` : "Ainda sem leitura do dispositivo."}
                tone={currentGeo ? "info" : "default"}
              />
            </div>
          </section>

          <section className="grid gap-3 sm:grid-cols-2">
            <GeoStatCard
              icon={MapPin}
              label="Parceiro"
              value={partner?.nome || "Parceiro vinculado"}
              helper={
                partnerHasCoordinates
                  ? `${formatGeoCoordinate(partner?.latitude)}, ${formatGeoCoordinate(partner?.longitude)}`
                  : "Sem coordenadas registradas"
              }
              tone={partnerHasCoordinates ? "default" : "warning"}
            />
            <GeoStatCard
              icon={Navigation}
              label="Seu dispositivo"
              value={currentGeo ? `${formatGeoCoordinate(currentGeo.lat)}, ${formatGeoCoordinate(currentGeo.lng)}` : "--"}
              helper={currentGeo ? `Precisao ${formatGeoAccuracy(currentGeo.accuracyM)}` : "Aguardando permissao/localizacao"}
              tone={currentGeo ? "default" : geoError ? "danger" : "info"}
            />
          </section>

          {(partner?.addressLabel || task?.descricaoTarefa) && (
            <section className="rounded-2xl border border-border bg-background/60 p-4 text-sm">
              <div className="flex items-start gap-3">
                <Route className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                <div className="space-y-2">
                  {task?.descricaoTarefa ? (
                    <p className="text-foreground">
                      <span className="font-semibold">Tarefa:</span> {task.descricaoTarefa}
                    </p>
                  ) : null}
                  {partner?.addressLabel ? (
                    <p className="text-muted-foreground">
                      <span className="font-semibold text-foreground">Endereco base:</span> {partner.addressLabel}
                    </p>
                  ) : null}
                </div>
              </div>
            </section>
          )}

          {fallbackAtivo ? (
            <section className="space-y-3 rounded-[24px] border border-warning/30 bg-warning/10 p-4">
              <div className="flex items-start gap-3">
                <ShieldAlert className="mt-0.5 h-5 w-5 shrink-0 text-warning" />
                <div className="space-y-1">
                  <h3 className="text-sm font-semibold text-foreground">
                    Fallback liberado para {actionLabel}
                  </h3>
                  <p className="text-xs text-muted-foreground">
                    {fallbackState?.mensagem || "A validacao por geolocalizacao falhou para esta tentativa."}
                  </p>
                </div>
              </div>

              <FormField
                label="Motivo do fallback"
                type="textarea"
                value={fallbackForm?.motivo || ""}
                onChange={(event) => onFallbackMotivoChange?.(event.target.value)}
                placeholder="Explique por que o check por GPS nao foi possivel"
              />

              <div className="space-y-1.5">
                <label className="text-sm font-medium text-foreground">
                  Imagens
                  {fallbackRequiresImage ? <span className="ml-1 text-destructive">*</span> : null}
                </label>
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={(event) => onFallbackFilesChange?.(Array.from(event.target.files || []))}
                  className="flex h-10 w-full rounded-lg border border-input bg-background px-3 py-2 text-sm"
                />
                {(fallbackForm?.files || []).length > 0 ? (
                  <p className="text-xs text-muted-foreground">
                    {(fallbackForm?.files || []).length} arquivo(s) selecionado(s).
                  </p>
                ) : null}
              </div>

              {fallbackPreviews.length > 0 ? (
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                  {fallbackPreviews.map((preview) => (
                    <div key={`drawer-fallback-${preview.url}`} className="overflow-hidden rounded-xl border border-border/70">
                      <img src={preview.url} alt={preview.name} className="h-24 w-full object-cover" />
                    </div>
                  ))}
                </div>
              ) : null}

              <div className="rounded-2xl border border-warning/20 bg-background/70 px-3 py-2 text-xs text-muted-foreground">
                <AlertTriangle className="mr-1 inline h-3.5 w-3.5" />
                {fallbackRequiresApproval
                  ? "Este fallback vai entrar na fila do admin para aprovacao."
                  : "Este fallback pode ser registrado sem aprovacao adicional."}
              </div>
            </section>
          ) : null}
        </DrawerBody>

        <DrawerFooter>
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <Button variant="outline" onClick={() => onOpenChange?.(false)} className="w-full sm:w-auto">
              Fechar
            </Button>
            {fallbackAtivo ? (
              <Button
                onClick={handleSubmitFallback}
                disabled={!canSubmitFallback}
                className="w-full sm:w-auto"
              >
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ActionIcon className="h-4 w-4" />}
                Enviar fallback
              </Button>
            ) : (
              <Button onClick={handleConfirm} disabled={!canConfirm} className="w-full sm:w-auto">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                Confirmar {actionLabel}
              </Button>
            )}
          </div>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
}
